import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import PermissionsEditor from '../components/PermissionsEditor';
import SettingsDocumentsTab from './SettingsDocumentsTab';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Settings,
  Users,
  Download,
  Upload,
  Database,
  Mail,
  Shield,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  User,
  Image,
  Palette,
  Layout,
  RotateCcw,
  FileText
} from 'lucide-react';

export default function SettingsPage() {
  const { API, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('aparencia');
  
  // Users state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'operador' });
  const [editingUser, setEditingUser] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState({});
  
  // Backup state
  const [backupSettings, setBackupSettings] = useState({
    frequencia: 'desativado',
    email_notificacao: '',
    notificar_por_email: false
  });
  const [backups, setBackups] = useState([]);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [lastBackupStatus, setLastBackupStatus] = useState(null);
  
  // Import state
  const [importModule, setImportModule] = useState('clientes');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importStats, setImportStats] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [substituirExistentes, setSubstituirExistentes] = useState(false);
  const [showImportResults, setShowImportResults] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1: select file, 2: preview, 3: import
  
  // Restore state
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  
  // App Settings state
  const [appSettings, setAppSettings] = useState({
    logo_url: null,
    app_title: 'GOLOC',
    app_subtitle: 'Sistema de Gestão de Locadora',
    login_background_url: null,
    login_background_color: '#1e3a5f',
    login_logo_position: 'center',
    login_form_position: 'center',
    login_show_title: true,
    login_custom_text: null
  });
  const [savingAppSettings, setSavingAppSettings] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);

  useEffect(() => {
    if (activeTab === 'usuarios') {
      loadUsers();
    } else if (activeTab === 'backup') {
      loadBackupSettings();
      loadBackups();
    } else if (activeTab === 'aparencia') {
      loadAppSettings();
    }
  }, [activeTab]);

  const loadAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/app`, { withCredentials: true });
      setAppSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error loading app settings:', error);
    }
  };

  const saveAppSettings = async () => {
    setSavingAppSettings(true);
    try {
      await axios.put(`${API}/settings/app`, appSettings, { withCredentials: true });
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingAppSettings(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/settings/app/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAppSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo atualizada!');
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao fazer upload da logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingBackground(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/settings/app/login-background`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAppSettings(prev => ({ ...prev, login_background_url: response.data.login_background_url }));
      toast.success('Imagem de fundo atualizada!');
    } catch (error) {
      toast.error('Erro ao fazer upload da imagem');
    } finally {
      setUploadingBackground(false);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/settings/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores podem ver usuários.');
      } else {
        toast.error('Erro ao carregar usuários');
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadBackupSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/backup`, { withCredentials: true });
      setBackupSettings(response.data);
    } catch (error) {
      console.error('Error loading backup settings:', error);
    }
  };

  const loadBackups = async () => {
    try {
      const response = await axios.get(`${API}/settings/backup/list`, { withCredentials: true });
      setBackups(response.data);
    } catch (error) {
      console.error('Error loading backups:', error);
    }
  };

  const saveBackupSettings = async () => {
    try {
      await axios.put(`${API}/settings/backup`, backupSettings, { withCredentials: true });
      toast.success('Configurações de backup salvas!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    }
  };

  const createBackupNow = async () => {
    setLoadingBackup(true);
    try {
      const response = await axios.post(`${API}/settings/backup/now`, {}, { withCredentials: true });
      toast.success('Backup criado com sucesso!');
      setLastBackupStatus({ status: 'success', id: response.data.backup_id, date: new Date() });
      loadBackups();
    } catch (error) {
      toast.error('Erro ao criar backup');
      setLastBackupStatus({ status: 'error', date: new Date() });
    } finally {
      setLoadingBackup(false);
    }
  };

  const downloadBackup = async (backupId) => {
    try {
      const response = await axios.get(`${API}/settings/backup/download/${backupId}`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_${backupId}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado!');
    } catch (error) {
      toast.error('Erro ao baixar backup');
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) {
      toast.error('Selecione um arquivo de backup');
      return;
    }
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const backupData = JSON.parse(e.target.result);
        await axios.post(`${API}/settings/restore`, backupData, { withCredentials: true });
        toast.success('Restauração concluída!');
        setShowRestoreDialog(false);
        setRestoreFile(null);
      };
      reader.readAsText(restoreFile);
    } catch (error) {
      toast.error('Erro ao restaurar backup');
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      await axios.post(`${API}/settings/users`, {
        name: newUser.name,
        email: newUser.email,
        role: newUser.role
      }, { withCredentials: true });
      
      toast.success('Usuário criado! Senha padrão: 0000');
      setShowUserModal(false);
      setNewUser({ email: '', name: '', role: 'operador' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`Resetar senha de ${userName} para 0000?`)) return;

    try {
      await axios.post(`${API}/settings/users/${userId}/reset-password`, {}, { withCredentials: true });
      toast.success('Senha resetada para 0000');
    } catch (error) {
      toast.error('Erro ao resetar senha');
    }
  };

  const handleEditPermissions = (user) => {
    setEditingUser(user);
    setEditingPermissions(user.permissions || {});
    setShowPermissionsModal(true);
  };

  const savePermissions = async () => {
    try {
      await axios.put(
        `${API}/settings/users/${editingUser.user_id}/permissions`,
        { permissions: editingPermissions },
        { withCredentials: true }
      );
      toast.success('Permissões atualizadas!');
      setShowPermissionsModal(false);
      loadUsers();
    } catch (error) {
      toast.error('Erro ao atualizar permissões');
    }
  };

  const deleteUser = async () => {
    try {
      await axios.delete(`${API}/settings/users/${selectedUser.user_id}`, { withCredentials: true });
      toast.success('Usuário excluído!');
      setShowDeleteUserDialog(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Selecione um arquivo para importar');
      return;
    }
    
    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = JSON.parse(e.target.result);
          
          // Call the appropriate import endpoint
          let endpoint = '';
          switch (importModule) {
            case 'clientes':
              endpoint = `${API}/import/clientes`;
              break;
            case 'veiculos':
              endpoint = `${API}/import/veiculos`;
              break;
            case 'contratos':
              endpoint = `${API}/import/contratos`;
              break;
            case 'faturas':
              endpoint = `${API}/import/faturas`;
              break;
            case 'manutencoes':
              endpoint = `${API}/import/manutencoes`;
              break;
            case 'movimentacoes':
              endpoint = `${API}/import/movimentacoes`;
              break;
            case 'completo':
              endpoint = `${API}/import/completo`;
              break;
            default:
              toast.error('Módulo de importação inválido');
              return;
          }
          
          const response = await axios.post(
            `${endpoint}?substituir=${substituirExistentes}`, 
            data, 
            { withCredentials: true }
          );
          
          setImportResults(response.data);
          setShowImportResults(true);
          
          if (response.data.importados > 0 || response.data.resumo?.total_importados > 0) {
            toast.success(`Importação concluída com sucesso!`);
          }
          
        } catch (err) {
          console.error('Import error:', err);
          toast.error(err.response?.data?.detail || 'Erro ao processar arquivo');
        }
      };
      reader.readAsText(importFile);
    } finally {
      setImporting(false);
    }
  };

  const analyzeFile = async () => {
    if (!importFile) {
      toast.error('Selecione um arquivo para analisar');
      return;
    }
    
    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = JSON.parse(e.target.result);
          const response = await axios.post(`${API}/import/analyze`, data, { withCredentials: true });
          setImportStats(response.data);
          setImportStep(2);
        } catch (err) {
          toast.error('Erro ao analisar arquivo. Verifique se é um JSON válido.');
        }
      };
      reader.readAsText(importFile);
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportStats(null);
    setImportResults(null);
    setImportStep(1);
    setShowImportResults(false);
  };

  const importModules = [
    { value: 'completo', label: 'Backup Completo', icon: Database, description: 'Importa todos os módulos de uma vez' },
    { value: 'clientes', label: 'Clientes', icon: Users, description: 'Cadastro de clientes' },
    { value: 'veiculos', label: 'Veículos', icon: Settings, description: 'Frota de veículos' },
    { value: 'contratos', label: 'Contratos', icon: Shield, description: 'Contratos de locação' },
    { value: 'faturas', label: 'Faturas', icon: Mail, description: 'Faturas e cobranças' },
    { value: 'manutencoes', label: 'Manutenções', icon: Settings, description: 'Histórico de manutenções' },
    { value: 'movimentacoes', label: 'Financeiro', icon: Database, description: 'Movimentações financeiras' }
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-[Manrope]">Configurações</h1>
        <p className="text-gray-500 text-sm">Gerencie usuários, backups e importações</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-6' : 'grid-cols-5'}`}>
          <TabsTrigger value="aparencia" data-testid="tab-aparencia">
            <Palette className="w-4 h-4 mr-2" />
            Aparência
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="usuarios" data-testid="tab-usuarios">
              <Users className="w-4 h-4 mr-2" />
              Usuários
            </TabsTrigger>
          )}
          <TabsTrigger value="documentos" data-testid="tab-documentos">
            <FileText className="w-4 h-4 mr-2" />
            Documentos
          </TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">
            <Database className="w-4 h-4 mr-2" />
            Backup
          </TabsTrigger>
          <TabsTrigger value="importar" data-testid="tab-importar">
            <Upload className="w-4 h-4 mr-2" />
            Importar
          </TabsTrigger>
          <TabsTrigger value="perfil" data-testid="tab-perfil">
            <User className="w-4 h-4 mr-2" />
            Perfil
          </TabsTrigger>
        </TabsList>

        {/* Aparência Tab */}
        <TabsContent value="aparencia" className="space-y-4">
          {/* Logo e Título */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5" />
                Logo e Título do App
              </CardTitle>
              <CardDescription>Personalize a identidade visual do sistema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload */}
              <div className="space-y-3">
                <Label>Logo do Sistema</Label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                    {appSettings.logo_url ? (
                      <img src={appSettings.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <Image className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('logo-upload').click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? 'Enviando...' : 'Alterar Logo'}
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG ou SVG (máx. 2MB)</p>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="app-title">Título do App</Label>
                  <Input
                    id="app-title"
                    value={appSettings.app_title || ''}
                    onChange={(e) => setAppSettings(prev => ({ ...prev, app_title: e.target.value }))}
                    placeholder="GOLOC"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="app-subtitle">Subtítulo</Label>
                  <Input
                    id="app-subtitle"
                    value={appSettings.app_subtitle || ''}
                    onChange={(e) => setAppSettings(prev => ({ ...prev, app_subtitle: e.target.value }))}
                    placeholder="Sistema de Gestão de Locadora"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tela de Login */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="w-5 h-5" />
                Personalização da Tela de Login
              </CardTitle>
              <CardDescription>Configure como a tela de login será apresentada</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Background */}
              <div className="space-y-3">
                <Label>Imagem de Fundo</Label>
                <div className="flex items-center gap-4">
                  <div className="w-32 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                    {appSettings.login_background_url ? (
                      <img src={appSettings.login_background_url} alt="Background" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs text-gray-400">Sem imagem</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      id="background-upload"
                      accept="image/*"
                      onChange={handleBackgroundUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('background-upload').click()}
                      disabled={uploadingBackground}
                    >
                      {uploadingBackground ? 'Enviando...' : 'Alterar Fundo'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Background Color */}
              <div className="space-y-2">
                <Label htmlFor="bg-color">Cor de Fundo (se sem imagem)</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="bg-color"
                    value={appSettings.login_background_color || '#1e3a5f'}
                    onChange={(e) => setAppSettings(prev => ({ ...prev, login_background_color: e.target.value }))}
                    className="w-12 h-10 rounded border cursor-pointer"
                  />
                  <Input
                    value={appSettings.login_background_color || '#1e3a5f'}
                    onChange={(e) => setAppSettings(prev => ({ ...prev, login_background_color: e.target.value }))}
                    className="w-32"
                    placeholder="#1e3a5f"
                  />
                </div>
              </div>

              {/* Positions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Posição da Logo</Label>
                  <Select 
                    value={appSettings.login_logo_position || 'center'} 
                    onValueChange={(v) => setAppSettings(prev => ({ ...prev, login_logo_position: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Esquerda</SelectItem>
                      <SelectItem value="center">Centro</SelectItem>
                      <SelectItem value="right">Direita</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Posição do Formulário</Label>
                  <Select 
                    value={appSettings.login_form_position || 'center'} 
                    onValueChange={(v) => setAppSettings(prev => ({ ...prev, login_form_position: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Esquerda</SelectItem>
                      <SelectItem value="center">Centro</SelectItem>
                      <SelectItem value="right">Direita</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Show Title */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="show-title"
                  checked={appSettings.login_show_title}
                  onCheckedChange={(checked) => setAppSettings(prev => ({ ...prev, login_show_title: checked }))}
                />
                <Label htmlFor="show-title">Exibir título e subtítulo na tela de login</Label>
              </div>

              {/* Custom Text */}
              <div className="space-y-2">
                <Label htmlFor="custom-text">Texto Personalizado (opcional)</Label>
                <Input
                  id="custom-text"
                  value={appSettings.login_custom_text || ''}
                  onChange={(e) => setAppSettings(prev => ({ ...prev, login_custom_text: e.target.value }))}
                  placeholder="Ex: Bem-vindo ao sistema de locação"
                />
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <Label>Pré-visualização</Label>
                <div 
                  className="w-full h-48 rounded-lg overflow-hidden relative"
                  style={{ 
                    backgroundColor: appSettings.login_background_url ? undefined : (appSettings.login_background_color || '#1e3a5f'),
                    backgroundImage: appSettings.login_background_url ? `url(${appSettings.login_background_url})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`bg-white/95 p-4 rounded-lg shadow-lg w-64 ${
                      appSettings.login_form_position === 'left' ? 'absolute left-8' :
                      appSettings.login_form_position === 'right' ? 'absolute right-8' : ''
                    }`}>
                      {appSettings.login_show_title && (
                        <div className="text-center mb-3">
                          <p className="font-bold text-gray-800">{appSettings.app_title || 'GOLOC'}</p>
                          <p className="text-xs text-gray-500">{appSettings.app_subtitle}</p>
                        </div>
                      )}
                      <div className="space-y-2">
                        <div className="h-6 bg-gray-100 rounded w-full"></div>
                        <div className="h-6 bg-gray-100 rounded w-full"></div>
                        <div className="h-6 bg-blue-500 rounded w-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <Button onClick={saveAppSettings} disabled={savingAppSettings} className="w-full">
                {savingAppSettings ? 'Salvando...' : 'Salvar Configurações de Aparência'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="usuarios" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Gestão de Usuários</CardTitle>
                <CardDescription>Controle de acesso e permissões</CardDescription>
              </div>
              <Button onClick={() => setShowUserModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Novo Usuário
              </Button>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>E-mail</TableHead>
                        <TableHead>Nível</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.user_id}>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                              {u.role === 'admin' ? 'Administrador' : 
                               u.role === 'gerente' ? 'Gerente' : 'Operador'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditPermissions(u)}
                                title="Gerenciar Permissões"
                              >
                                <Shield className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResetPassword(u.user_id, u.name)}
                                title="Resetar Senha"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              {u.user_id !== user?.user_id && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setShowDeleteUserDialog(true);
                                  }}
                                  title="Excluir Usuário"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Informação:</strong> Todos os novos usuários são criados com a senha padrão <strong>0000</strong>. 
                      No primeiro acesso, o usuário será obrigado a trocar a senha.
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documentos Tab */}
        <TabsContent value="documentos" className="space-y-4">
          <SettingsDocumentsTab />
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup" className="space-y-4">
          {/* Backup Status Alert */}
          {lastBackupStatus?.status === 'error' && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="font-medium text-red-800">Erro no último backup</p>
                  <p className="text-sm text-red-600">
                    {lastBackupStatus.date?.toLocaleString('pt-BR')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Auto Backup Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Backup Automático</CardTitle>
                <CardDescription>Configure backups automáticos do sistema</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Frequência</Label>
                  <Select 
                    value={backupSettings.frequencia} 
                    onValueChange={(v) => setBackupSettings({ ...backupSettings, frequencia: v })}
                  >
                    <SelectTrigger data-testid="backup-frequencia">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desativado">Desativado</SelectItem>
                      <SelectItem value="diario">Diário</SelectItem>
                      <SelectItem value="semanal">Semanal</SelectItem>
                      <SelectItem value="mensal">Mensal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="notificar_email"
                    checked={backupSettings.notificar_por_email}
                    onCheckedChange={(checked) => setBackupSettings({ ...backupSettings, notificar_por_email: checked })}
                  />
                  <Label htmlFor="notificar_email" className="font-normal cursor-pointer">
                    Notificar por e-mail
                  </Label>
                </div>

                {backupSettings.notificar_por_email && (
                  <div>
                    <Label>E-mail para notificação</Label>
                    <Input
                      type="email"
                      value={backupSettings.email_notificacao || ''}
                      onChange={(e) => setBackupSettings({ ...backupSettings, email_notificacao: e.target.value })}
                      placeholder="email@exemplo.com"
                      data-testid="backup-email"
                    />
                  </div>
                )}

                <Button onClick={saveBackupSettings} className="w-full">
                  Salvar Configurações
                </Button>
              </CardContent>
            </Card>

            {/* Manual Backup */}
            <Card>
              <CardHeader>
                <CardTitle>Backup Manual</CardTitle>
                <CardDescription>Crie ou restaure backups manualmente</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  onClick={createBackupNow} 
                  disabled={loadingBackup}
                  className="w-full"
                  data-testid="create-backup-button"
                >
                  {loadingBackup ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Gerar Backup Agora
                    </>
                  )}
                </Button>

                <div className="border-t pt-4">
                  <Label className="mb-2 block">Restaurar Backup</Label>
                  <div 
                    className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
                    onClick={() => document.getElementById('restore-input').click()}
                  >
                    <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-600">
                      Clique para selecionar arquivo JSON
                    </p>
                    <input
                      id="restore-input"
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={(e) => {
                        setRestoreFile(e.target.files[0]);
                        setShowRestoreDialog(true);
                      }}
                    />
                  </div>
                  <p className="text-xs text-red-500 mt-2">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    Atenção: A restauração pode substituir dados existentes.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Backup List */}
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Backups</CardTitle>
            </CardHeader>
            <CardContent>
              {backups.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum backup encontrado</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Criado por</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backups.map((backup) => (
                      <TableRow key={backup.backup_id}>
                        <TableCell className="font-mono text-sm">{backup.backup_id}</TableCell>
                        <TableCell>
                          {new Date(backup.created_at).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell>{backup.created_by}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadBackup(backup.backup_id)}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Baixar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Import Tab */}
        <TabsContent value="importar" className="space-y-4">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className={`flex items-center gap-2 ${importStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>1</div>
              <span className="text-sm font-medium">Selecionar Arquivo</span>
            </div>
            <div className={`w-12 h-0.5 ${importStep >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center gap-2 ${importStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>2</div>
              <span className="text-sm font-medium">Pré-visualizar</span>
            </div>
            <div className={`w-12 h-0.5 ${importStep >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center gap-2 ${importStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${importStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>3</div>
              <span className="text-sm font-medium">Importar</span>
            </div>
          </div>

          {/* Step 1: Select File */}
          {importStep === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Importar Dados de Backup
                </CardTitle>
                <CardDescription>
                  Selecione um arquivo JSON de backup do sistema antigo para migrar os dados
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* File Upload Area */}
                <div 
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                  onClick={() => document.getElementById('import-file-input').click()}
                  data-testid="import-dropzone"
                >
                  <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  {importFile ? (
                    <>
                      <p className="text-lg font-medium text-green-600">{importFile.name}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {(importFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-lg font-medium text-gray-700">Clique para selecionar arquivo</p>
                      <p className="text-sm text-gray-500 mt-1">ou arraste e solte aqui</p>
                      <p className="text-xs text-gray-400 mt-2">Formatos aceitos: .json</p>
                    </>
                  )}
                  <input
                    id="import-file-input"
                    type="file"
                    accept=".json"
                    className="hidden"
                    onChange={(e) => {
                      setImportFile(e.target.files[0]);
                      setImportStats(null);
                    }}
                  />
                </div>

                {/* Module Selection */}
                <div>
                  <Label className="mb-3 block">Selecione o que importar:</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {importModules.map((m) => (
                      <Card 
                        key={m.value} 
                        className={`p-4 cursor-pointer transition-all ${
                          importModule === m.value 
                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                            : 'hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => setImportModule(m.value)}
                        data-testid={`import-module-${m.value}`}
                      >
                        <m.icon className={`w-6 h-6 mb-2 ${importModule === m.value ? 'text-blue-600' : 'text-gray-400'}`} />
                        <p className={`font-medium text-sm ${importModule === m.value ? 'text-blue-700' : 'text-gray-700'}`}>{m.label}</p>
                        <p className="text-xs text-gray-500 mt-1">{m.description}</p>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end pt-4 border-t">
                  <Button 
                    onClick={analyzeFile} 
                    disabled={!importFile || importing}
                    data-testid="analyze-file-button"
                  >
                    {importing ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Analisando...
                      </>
                    ) : (
                      <>
                        Analisar Arquivo
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Preview */}
          {importStep === 2 && importStats && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Arquivo Analisado
                </CardTitle>
                <CardDescription>
                  Revise os dados antes de importar
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Metadata */}
                {importStats.metadata && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Informações do Backup</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Data do Backup:</span>
                        <p className="font-medium">
                          {importStats.metadata.data_backup 
                            ? new Date(importStats.metadata.data_backup).toLocaleString('pt-BR')
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Sistema:</span>
                        <p className="font-medium">{importStats.metadata.sistema || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Versão:</span>
                        <p className="font-medium">{importStats.metadata.versao || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Statistics */}
                <div>
                  <h4 className="font-semibold mb-3">Registros Encontrados</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Object.entries(importStats.estatisticas || {}).map(([key, stats]) => (
                      <Card key={key} className={`p-4 ${stats.total > 0 ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
                        <p className="text-sm text-gray-600 capitalize">{key.replace('_', ' ')}</p>
                        <p className={`text-2xl font-bold ${stats.total > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                          {stats.total}
                        </p>
                        {stats.total > 0 && (
                          <p className="text-xs text-gray-500 mt-1">{stats.campos?.length || 0} campos</p>
                        )}
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-yellow-800">Opções de Importação</h4>
                      <div className="mt-3 flex items-center gap-2">
                        <Checkbox 
                          id="substituir-existentes"
                          checked={substituirExistentes}
                          onCheckedChange={setSubstituirExistentes}
                        />
                        <Label htmlFor="substituir-existentes" className="font-normal cursor-pointer text-sm">
                          Substituir registros existentes (se CPF/CNPJ ou ID já existir no sistema)
                        </Label>
                      </div>
                      <p className="text-xs text-yellow-700 mt-2">
                        Se desmarcado, registros existentes serão ignorados durante a importação.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-between pt-4 border-t">
                  <Button variant="outline" onClick={resetImport}>
                    Voltar
                  </Button>
                  <Button 
                    onClick={() => {
                      setImportStep(3);
                      handleImport();
                    }}
                    disabled={importing}
                    data-testid="start-import-button"
                  >
                    {importing ? (
                      <>
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Iniciar Importação
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Results */}
          {importStep === 3 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {importResults ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      Importação Concluída
                    </>
                  ) : (
                    <>
                      <Clock className="w-5 h-5 text-blue-500 animate-spin" />
                      Importando Dados...
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {importing && !importResults && (
                  <div className="flex flex-col items-center py-8">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
                    <p className="mt-4 text-gray-600">Processando registros...</p>
                    <p className="text-sm text-gray-400">Isso pode levar alguns minutos</p>
                  </div>
                )}

                {importResults && (
                  <>
                    {/* Summary for complete import */}
                    {importResults.resumo && (
                      <div className="grid grid-cols-3 gap-4">
                        <Card className="p-4 bg-green-50 border-green-200">
                          <p className="text-sm text-gray-600">Importados</p>
                          <p className="text-3xl font-bold text-green-600">{importResults.resumo.total_importados}</p>
                        </Card>
                        <Card className="p-4 bg-yellow-50 border-yellow-200">
                          <p className="text-sm text-gray-600">Ignorados</p>
                          <p className="text-3xl font-bold text-yellow-600">{importResults.resumo.total_ignorados}</p>
                        </Card>
                        <Card className="p-4 bg-red-50 border-red-200">
                          <p className="text-sm text-gray-600">Erros</p>
                          <p className="text-3xl font-bold text-red-600">{importResults.resumo.total_erros}</p>
                        </Card>
                      </div>
                    )}

                    {/* Details for complete import */}
                    {importResults.detalhes && (
                      <div>
                        <h4 className="font-semibold mb-3">Detalhes por Módulo</h4>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Módulo</TableHead>
                              <TableHead className="text-center">Total</TableHead>
                              <TableHead className="text-center">Importados</TableHead>
                              <TableHead className="text-center">Ignorados</TableHead>
                              <TableHead className="text-center">Erros</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {importResults.detalhes.map((detail, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium capitalize">{detail.modulo}</TableCell>
                                <TableCell className="text-center">{detail.total_registros}</TableCell>
                                <TableCell className="text-center text-green-600 font-medium">{detail.importados}</TableCell>
                                <TableCell className="text-center text-yellow-600">{detail.ignorados}</TableCell>
                                <TableCell className="text-center text-red-600">{detail.erros?.length || 0}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {/* Single module result */}
                    {importResults.modulo && !importResults.detalhes && (
                      <div className="grid grid-cols-3 gap-4">
                        <Card className="p-4 bg-green-50 border-green-200">
                          <p className="text-sm text-gray-600">Importados</p>
                          <p className="text-3xl font-bold text-green-600">{importResults.importados}</p>
                        </Card>
                        <Card className="p-4 bg-yellow-50 border-yellow-200">
                          <p className="text-sm text-gray-600">Ignorados</p>
                          <p className="text-3xl font-bold text-yellow-600">{importResults.ignorados}</p>
                        </Card>
                        <Card className="p-4 bg-gray-50">
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="text-3xl font-bold text-gray-600">{importResults.total_registros}</p>
                        </Card>
                      </div>
                    )}

                    {/* Errors */}
                    {importResults.erros && importResults.erros.length > 0 && (
                      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                        <h4 className="font-semibold text-red-800 mb-2">Erros Encontrados</h4>
                        <div className="max-h-40 overflow-y-auto text-sm">
                          {importResults.erros.slice(0, 20).map((err, idx) => (
                            <p key={idx} className="text-red-600 py-1 border-b border-red-100 last:border-0">
                              {err}
                            </p>
                          ))}
                          {importResults.erros.length > 20 && (
                            <p className="text-red-500 pt-2">... e mais {importResults.erros.length - 20} erros</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Action */}
                    <div className="flex gap-3 justify-center pt-4 border-t">
                      <Button onClick={resetImport}>
                        Nova Importação
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Help Section */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Como funciona a importação?</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Selecione um arquivo JSON de backup do sistema antigo</li>
                <li>• O sistema irá analisar e mostrar um resumo dos dados encontrados</li>
                <li>• Escolha se deseja importar um módulo específico ou todos os dados</li>
                <li>• Registros com CPF/CNPJ ou ID duplicado podem ser ignorados ou substituídos</li>
                <li>• Após a importação, verifique os dados nas respectivas páginas do sistema</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="perfil" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Meu Perfil</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                  {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{user?.name}</h3>
                  <p className="text-gray-500">{user?.email}</p>
                  <Badge className="mt-1">
                    {user?.role === 'admin' ? 'Administrador' : 'Usuário'}
                  </Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">ID do Usuário</p>
                  <p className="font-mono">{user?.user_id}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Usuário</DialogTitle>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4">
            <div>
              <Label htmlFor="user_name">Nome</Label>
              <Input
                id="user_name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required
                data-testid="new-user-name"
              />
            </div>
            <div>
              <Label htmlFor="user_email">E-mail</Label>
              <Input
                id="user_email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label htmlFor="user_role">Função</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operador">Operador</SelectItem>
                  <SelectItem value="gerente">Gerente</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Nota:</strong> O usuário será criado com a senha padrão <strong>0000</strong> e será obrigado a alterá-la no primeiro acesso.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUserModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-user-button">
                Criar Usuário
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={showDeleteUserDialog} onOpenChange={setShowDeleteUserDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o usuário "{selectedUser?.name}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Dialog */}
      <AlertDialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar Backup</AlertDialogTitle>
            <AlertDialogDescription>
              <AlertTriangle className="w-5 h-5 inline mr-2 text-yellow-600" />
              Atenção: A restauração pode substituir ou adicionar dados ao banco atual. Esta ação deve ser manuseada com cautela.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRestoreFile(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore}>
              Restaurar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permissions Modal */}
      <Dialog open={showPermissionsModal} onOpenChange={setShowPermissionsModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gerenciar Permissões - {editingUser?.name}</DialogTitle>
          </DialogHeader>
          <PermissionsEditor 
            permissions={editingPermissions} 
            onChange={setEditingPermissions}
            disabled={false}
          />
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowPermissionsModal(false)}>
              Cancelar
            </Button>
            <Button onClick={savePermissions}>
              Salvar Permissões
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
